import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Copy, CheckCircle2, XCircle, Users2, Trash2 } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { api } from "@/lib/api";

/**
 * Sezione amministrativa per gestire i codici invito della beta.
 * Il parent deve mostrarla SOLO se l'utente ha user.is_admin === true.
 */
export default function InviteCodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", comp: true, max_uses: "" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // codice in attesa di conferma cancellazione
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.adminListInvites();
      setCodes(list || []);
    } catch (e) {
      toast.error("Impossibile caricare i codici", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCode = async (e) => {
    e?.preventDefault?.();
    if (!form.label.trim()) {
      toast.error("Inserisci un&apos;etichetta descrittiva per il codice");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        label: form.label.trim(),
        comp: !!form.comp,
      };
      if (form.code.trim()) payload.code = form.code.trim().toUpperCase();
      if (form.max_uses !== "" && !isNaN(Number(form.max_uses))) payload.max_uses = Number(form.max_uses);
      const created = await api.adminCreateInvite(payload);
      toast.success(`Codice ${created.code} creato`);
      setForm({ code: "", label: "", comp: true, max_uses: "" });
      await load();
    } catch (e) {
      toast.error("Errore creazione codice", { description: e?.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await api.adminToggleInvite(code, active);
      setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, active } : c)));
      toast.success(active ? `Codice ${code} riattivato` : `Codice ${code} disattivato`);
    } catch (e) {
      toast.error("Errore aggiornamento", { description: e?.response?.data?.detail || e.message });
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch (_e) { /* noop */ }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.adminDeleteInvite(pendingDelete.code);
      // Rimuovi dalla lista in loco: la UI si aggiorna senza reload.
      setCodes((prev) => prev.filter((c) => c.code !== pendingDelete.code));
      toast.success(`Codice ${pendingDelete.code} cancellato`);
      setPendingDelete(null);
    } catch (e) {
      toast.error("Errore cancellazione codice", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="invite-codes-admin" className="mag-card p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[#EDE7F6] text-[#4A2E7A] flex items-center justify-center flex-shrink-0">
          <KeyRound size={18} />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Codici invito (beta)
          </h2>
          <p className="text-sm text-[#8E918C] mt-1">
            Riservato all&apos;admin. Genera codici invito per attivare nuovi account Google.
            I codici marcati <em>&quot;accesso gratuito a vita&quot;</em> registrano l&apos;utente come <code>is_comp=true</code>{" "}
            (esente da futuri pagamenti).
          </p>
        </div>
      </div>

      {/* Form crea codice */}
      <form onSubmit={createCode} className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-[#EAE7E1] rounded-xl p-4 mb-6 bg-white">
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Etichetta / destinatario *</Label>
          <Input
            data-testid="invite-form-label"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder='Es: "Amici bar centro" oppure "Test giornalisti"'
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Codice (opzionale)</Label>
          <Input
            data-testid="invite-form-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="Lascia vuoto per generarne uno"
            className="uppercase font-mono"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Numero max di usi</Label>
          <Input
            data-testid="invite-form-maxuses"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value.replace(/[^0-9]/g, "") })}
            placeholder="Vuoto = illimitato"
            inputMode="numeric"
          />
        </div>
        <div className="flex items-center gap-3 sm:col-span-2 border border-[#EAE7E1] rounded-lg px-3 py-2">
          <Switch
            data-testid="invite-form-comp"
            checked={form.comp}
            onCheckedChange={(v) => setForm({ ...form, comp: v })}
          />
          <div className="text-sm">
            <div className="font-medium">Accesso gratuito a vita (comp)</div>
            <div className="text-xs text-[#8E918C]">
              Gli account creati con questo codice non pagheranno mai (utile per beta tester, amici, stampa).
            </div>
          </div>
        </div>
        <div className="sm:col-span-2">
          <Button
            type="submit"
            data-testid="invite-form-submit"
            disabled={creating}
            className="bg-[#2E4A3B] hover:bg-[#243c2f] text-white"
          >
            <Plus size={16} className="mr-1.5" />
            {creating ? "Creazione..." : "Crea codice"}
          </Button>
        </div>
      </form>

      {/* Lista codici */}
      <div className="space-y-2">
        {loading && <div className="text-sm text-[#8E918C]">Caricamento...</div>}
        {!loading && codes.length === 0 && (
          <div className="text-sm text-[#8E918C]">Nessun codice ancora creato.</div>
        )}
        {codes.map((c) => {
          const usesLabel = c.max_uses ? `${c.used_count || 0} / ${c.max_uses}` : `${c.used_count || 0} / ∞`;
          return (
            <div
              key={c.code}
              data-testid={`invite-row-${c.code}`}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${c.active ? "border-[#EAE7E1] bg-white" : "border-[#EAE7E1] bg-[#F5F3EE] opacity-70"}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => copyCode(c.code)}
                    className="font-mono text-sm font-semibold text-[#2E4A3B] hover:underline flex items-center gap-1"
                    data-testid={`invite-copy-${c.code}`}
                    title="Copia codice"
                  >
                    {c.code}
                    {copied === c.code ? <CheckCircle2 size={14} className="text-green-600" /> : <Copy size={14} className="opacity-50" />}
                  </button>
                  {c.comp && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#EDE7F6] text-[#4A2E7A] font-semibold">
                      COMP (gratis a vita)
                    </span>
                  )}
                  {!c.active && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-red-50 text-red-700 font-semibold flex items-center gap-1">
                      <XCircle size={11} /> disattivato
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#5C5F5A] mt-0.5">{c.label}</div>
                <div className="text-xs text-[#8E918C] mt-1 flex items-center gap-1">
                  <Users2 size={12} /> Usi: <strong className="text-[#2E4A3B]">{usesLabel}</strong>
                  {Array.isArray(c.used_by) && c.used_by.length > 0 && (
                    <span className="ml-2 truncate">
                      · Ultimo: {c.used_by[c.used_by.length - 1]?.email}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center">
                <Switch
                  checked={!!c.active}
                  onCheckedChange={(v) => toggleActive(c.code, v)}
                  data-testid={`invite-toggle-${c.code}`}
                />
                <span className="text-xs text-[#5C5F5A]">{c.active ? "Attivo" : "Off"}</span>
                <button
                  type="button"
                  onClick={() => setPendingDelete(c)}
                  data-testid={`invite-delete-${c.code}`}
                  title="Cancella codice"
                  className="ml-1 h-9 w-9 inline-flex items-center justify-center rounded-md text-[#8E3B2A] hover:bg-[#FCEAE8] transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog di conferma cancellazione */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="invite-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellare questo codice invito?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  Codice:{" "}
                  <code className="font-mono font-semibold text-[#2E4A3B]">
                    {pendingDelete?.code}
                  </code>
                </div>
                <div>
                  Etichetta:{" "}
                  <span className="font-medium text-[#3E4034]">
                    {pendingDelete?.label || "—"}
                  </span>
                </div>
                <div>
                  Utilizzato da:{" "}
                  <strong className="text-[#2E4A3B]">
                    {(pendingDelete?.used_count || 0)}{" "}
                    {(pendingDelete?.used_count || 0) === 1 ? "persona" : "persone"}
                  </strong>
                </div>
                <div className="pt-2 text-[#8E3B2A]">
                  Operazione non annullabile. Gli account già registrati con questo codice restano validi e mantengono l&apos;accesso gratuito a vita, ma perderai la lista di chi lo ha usato.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-delete-cancel" disabled={deleting}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="invite-delete-confirm"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#8E3B2A] hover:bg-[#6E2A1E] text-white"
            >
              {deleting ? "Cancellazione..." : "Cancella definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
